// The routes that hang directly off upstream's ROOT abstract state rather than off any of the
// three that render chrome — `chrome-extension.post` and `chrome-extension.my-account`.
//
// ⚠️ THE DISTINCTION IS VISIBLE, WHICH IS WHY IT GETS ITS OWN FILE. A screen under
// `chrome-extension.full-page-dialog` renders inside an overlay and a fixed-width column; one
// under `chrome-extension.extension-tabs` renders inside the tab strip. These two render inside
// neither: `post` is a whole browser tab an OAuth provider redirected to, and `my-account` is a
// state that draws nothing at all.
//
// ⚠️ AND A ROUTE WITH NO LAYOUT HAS TO APPLY ITS OWN CHROME. Every other route in this app sits
// under a layout that calls `useRouteChrome`; these do not, and without it `bodyClass:
// 'no-min-width'` never reaches the body — the main app's 1200px min-width wins, the page gets a
// horizontal scrollbar, and the scrollbar takes ~14px off the viewport that `post` centres
// itself in. Seven pixels of drift on a screen that is otherwise identical.
import { useEffect, useState, type ReactElement } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { emailIntegrationConnectUrl, fetchViewModel, validateAndSaveExchange } from '@woo-ext/api'
import { EmailSignIn, parseEmailServerType, PostPage, type EmailServerType } from '@woo-ext/ui'
import { AutomationCriteria } from './AutomationCriteria.tsx'
import { SessionUnavailable } from './SessionUnavailable.tsx'
import { useAuthGuard } from '@woo-ext/ui'
import { useRouteChrome } from './useRouteChrome.ts'
import { PostEventType, publishPostEvent } from '../postChannel.ts'

/** `ExtensionUserGroup` — the membership keys `careero.groups` is indexed by. */
const CANDIDATES_GROUP = 'CANDIDATES'
const CLIENT_CONTACT_GROUP = 'CLIENT_CONTACT'

const CANDIDATES_REVIEW = '/chrome-extension/extension/shortlist/review'
const CLIENT_CONTACTS_REVIEW = '/chrome-extension/extension/client-contact/review'

/**
 * `ExtensionProductService.getDefaultExtensionProduct` — which product a user lands in.
 *
 * ⚠️ CANDIDATES WINS A TIE, AND AN ABSENT MAP IS A TIE. Upstream returns Candidates when there
 * are no groups at all, ClientContacts only when the user has that group and NOT candidates,
 * and Candidates when they have both. Reading it as "contacts if they have contacts" sends a
 * dual-group user to the wrong product on every visit.
 */
export function defaultProductPath(groups: Record<string, boolean> | undefined): string {
  if (groups === undefined || groups[CLIENT_CONTACT_GROUP] !== true) return CANDIDATES_REVIEW
  if (groups[CANDIDATES_GROUP] !== true) return CLIENT_CONTACTS_REVIEW
  return CANDIDATES_REVIEW
}

/**
 * `chrome-extension.my-account` — a state that renders nothing and redirects.
 *
 * ⚠️ THE STORED PRODUCT IS NOT READ YET, AND THE REFERENCE CANNOT TELL. Upstream first asks
 * `ExtensionStorageService` for the active product, per HOST, and only falls back to the group
 * default when there is none — so a recruiter who switched to contacts lands back in contacts.
 * That storage is the shell's (Phase 6), and the capture had none either: its reference is four
 * widths of the candidates review screen, which is exactly what the fallback produces. So this
 * matches the reference AND is knowingly short of upstream, which the manifest records.
 */
export function MyAccountRoute(): ReactElement {
  useRouteChrome()
  const [target, setTarget] = useState<string | undefined>(undefined)
  useEffect(() => {
    let live = true
    fetchViewModel().then(
      (model) => {
        if (live) setTarget(defaultProductPath(model.groups))
      },
      () => {
        // The guard on the destination is what handles a tenant that cannot answer; sending the
        // user to the candidates review is upstream's own default and fails the same way.
        if (live) setTarget(CANDIDATES_REVIEW)
      },
    )
    return () => {
      live = false
    }
  }, [])
  // Nothing renders while the product resolves, because upstream's state has no template: the
  // screen a user sees is the one it redirects to.
  return target === undefined ? <div /> : <Navigate to={target} replace />
}

/** `chrome-extension.post` — the tab an OAuth provider redirects back to. */
export function PostRoute(): ReactElement {
  useRouteChrome()
  const [params] = useSearchParams()
  const type = params.get('type')
  const phase = params.get('phase')
  // `$onInit`'s `create(PostChannel).postMessage({ type })` — the point of the page. The side view
  // that started the connect is listening (`subscribePostEvents`) and moves on to the composer.
  return (
    <PostPage
      {...(type === null ? {} : { type })}
      {...(phase === null ? {} : { phase })}
      onConnected={publishPostEvent}
    />
  )
}

/**
 * `chrome-extension.email-signin` — "Please connect your work email" as a WHOLE PAGE.
 *
 * ⚠️ THE SAME TEMPLATE AS THE SIDE-VIEW PANEL, IN A DIFFERENT ELEMENT. Upstream renders
 * `<extension-user-external-email-signin>` here and `<extension-alert-candidates-email-signin>`
 * in the four side views; the markup between them differs only by the ✕, which is `ng-if` on an
 * alert id this route does not have. The stylesheets are written against the two element names,
 * so the element is the port — see `hostElement`.
 *
 * ⚠️ AND THIS ONE IS GUARDED. It is the only route in this file that declares `privAccess`, and
 * its reference was captured WITH a session for that reason. `useAuthGuard` here rather than in
 * a layout, because this route has none.
 */
export function EmailSignInRoute(): ReactElement {
  useRouteChrome()
  const guard = useAuthGuard({ requireSession: true })
  const [params] = useSearchParams()
  const [forced, setForced] = useState<EmailServerType | undefined>(undefined)
  useEffect(() => {
    let live = true
    fetchViewModel().then(
      (model) => {
        // `careero.forceEmailServerType` — a tenant that has picked one mail provider for
        // everyone. It decides which of the three buttons show; see `emailSignInProviders`.
        if (live) setForced(parseEmailServerType(model.forceEmailServerType?.toString()))
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [])

  if (guard.status === 'unavailable') return <SessionUnavailable onRetry={guard.retry} />
  if (guard.status !== 'allowed') return <div />
  const emailServerType = parseEmailServerType(params.get('emailServerType'))
  return (
    <EmailSignIn
      hostElement="extension-user-external-email-signin"
      {...(emailServerType === undefined ? {} : { emailServerType })}
      {...(forced === undefined ? {} : { forceEmailServerType: forced })}
      // `onGmailConnectClicked` / `onOutlookConnectClicked` (external-email-signin.page.component).
      //
      // ⚠️ THIS SCREEN HAD NO HANDLER AT ALL, so all three buttons were drawn and inert (reported
      // 2026-10-28). Upstream opens the connect URL in THIS tab (`'_self'`), not a new one as the
      // side-view panel does, and always with `phase=EMAIL_SIGNUP`: there is no alert here.
      // `emailIntegrationConnectUrl` adds the `origin` careero needs to send the user back HERE
      // rather than to woo.io. Exchange raises its login dialog instead (`onExchangeSignIn`).
      onConnect={(provider) => {
        if (provider === 'exchange') return
        window.open(
          emailIntegrationConnectUrl(provider === 'gmail' ? 'google' : 'outlook', { phase: 'EMAIL_SIGNUP' }),
          '_self',
        )
      }}
      onExchangeSignIn={async (username, password) =>
        (await validateAndSaveExchange({ username, password })).success
      }
      // Announced exactly as the side view's is; nothing on this page listens, as upstream.
      onExchangeConnected={() => {
        publishPostEvent(PostEventType.ExchangeConnected)
      }}
    />
  )
}

export const standaloneRoutes = [
  // `chrome-extension.automation-criteria` — off the root state too, and big enough for its own
  // file. See routes/AutomationCriteria.tsx.
  { path: '/chrome-extension/automation-criteria', element: <AutomationCriteria /> },
  { path: '/chrome-extension/post', element: <PostRoute /> },
  { path: '/chrome-extension/myaccount', element: <MyAccountRoute /> },
  { path: '/chrome-extension/email-signin', element: <EmailSignInRoute /> },
]
